import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Modal, Input, message } from 'antd';
import { api, fmt } from '../api';
import { CATEGORY_OPTIONS } from '../labels';

// 천단위 콤마 자동 표기 input — 내부 값은 number, 표시는 "1,000,000".
function MoneyInput({
  value, onChange, placeholder, style,
}: {
  value: number;
  onChange: (n: number) => void;
  placeholder?: string;
  style?: React.CSSProperties;
}) {
  const display = Number.isFinite(value) ? value.toLocaleString('ko-KR') : '';
  return (
    <input
      type="text"
      inputMode="numeric"
      value={display}
      onChange={(e) => {
        const raw = e.target.value.replace(/[^0-9]/g, '');
        onChange(raw === '' ? 0 : Number(raw));
      }}
      placeholder={placeholder}
      style={style}
    />
  );
}

type FormState = {
  mid: string; name: string; start_date: string; term_days: number;
  deposit_amount: number; purchase_amount: number; fee_rate: number;
  purchase_not_applicable: boolean;  // 문서 09-01 §9: 매입액 미해당 플래그
  category: 'repay' | 'normal' | 'bad';
  payout_method: 'bank' | 'wallet';
  people_id: string;
  payout_bank: string; payout_account: string; payout_holder: string; payout_memo: string;
  repay_bank: string; repay_account: string; repay_holder: string; repay_memo: string;
  ceo_name: string; contact_phone: string; business_no: string; business_address: string;
};

// 상환 계좌 기본값 — 운영 정책상 모든 가맹점이 같은 계좌로 상환금을 입금.
// 추후 변경될 수 있고, 가맹점별로 다른 계좌가 필요한 경우 폼에서 직접 수정 가능.
const DEFAULT_REPAY_ACCOUNT = {
  bank:    '농협',
  account: '355-0095-8065-63',
  holder:  '유한회사 서포트리',
};

const REPAY_LS_KEY = 'supportree.repayAccount.default';
type RepayDefault = { bank: string; account: string; holder: string };
const loadRepayDefault = (): RepayDefault => {
  try {
    const s = localStorage.getItem(REPAY_LS_KEY);
    if (s) {
      const d = JSON.parse(s);
      if (d.bank || d.account || d.holder) return d;
    }
  } catch {}
  return { ...DEFAULT_REPAY_ACCOUNT };
};
const saveRepayDefault = (d: RepayDefault) => {
  try { localStorage.setItem(REPAY_LS_KEY, JSON.stringify(d)); } catch {}
};

const empty: FormState = (() => {
  const d = loadRepayDefault();
  return {
    mid: '', name: '', start_date: new Date().toISOString().slice(0, 10),
    term_days: 0, deposit_amount: 0, purchase_amount: 0,
    purchase_not_applicable: false,
    fee_rate: 0,
    category: 'repay',
    payout_method: 'bank',
    people_id: '',
    payout_bank: '', payout_account: '', payout_holder: '', payout_memo: '',
    repay_bank: d.bank, repay_account: d.account, repay_holder: d.holder, repay_memo: '',
    ceo_name: '', contact_phone: '', business_no: '', business_address: '',
  };
})();

// 필수 표시(*) — 이 스타일을 라벨 옆에 사용.
const Req = () => <span style={{ color: 'var(--warn)', marginLeft: 2 }} title="필수 입력">*</span>;

// 입력값 오류 — 필드별 인라인 표시용. 서버 도달 전에 걸러낸다.
type FormErrors = Partial<Record<
  'mid'|'name'|'start_date'|'term_days'|'purchase_amount'|'deposit_amount'|'fee_rate'|
  'category'|'payout_method'|'people_id'|'business_no',
  string
>>;

function validateForm(f: FormState): FormErrors {
  const e: FormErrors = {};
  if (!f.mid.trim())          e.mid = 'MID를 입력해 주세요.';
  else if (!/^[a-zA-Z0-9._-]+$/.test(f.mid.trim())) e.mid = 'MID는 영문/숫자/._- 만 허용됩니다.';
  if (!f.name.trim())         e.name = '가맹점명을 입력해 주세요.';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f.start_date)) e.start_date = '시작일을 선택해 주세요.';
  if (!(f.term_days > 0) && f.category === 'repay' && f.purchase_amount > 0) {
    e.term_days = '반환 진행 중(매입액 > 0)인 가맹점은 약정 기간이 필요합니다.';
  } else if (f.term_days < 0) {
    e.term_days = '약정 기간은 0 이상이어야 합니다.';
  }
  if (f.purchase_amount < 0) e.purchase_amount = '매입액은 0 이상이어야 합니다.';
  if (f.deposit_amount < 0)  e.deposit_amount = '입금액은 0 이상이어야 합니다.';
  if (!(f.fee_rate >= 0 && f.fee_rate <= 100)) e.fee_rate = '수수료율은 0 ~ 100 사이여야 합니다.';
  if (f.payout_method === 'wallet' && !f.people_id.trim()) e.people_id = '지갑정산은 가맹점 피플 ID가 필수입니다.';
  const bnoDigits = f.business_no.replace(/\D/g, '');
  if (f.business_no.trim() && bnoDigits.length !== 10) {
    e.business_no = '사업자등록번호는 10자리 숫자여야 합니다. (예: 123-45-67890)';
  }
  return e;
}

// 등록 최종 확인 다이얼로그용 요약 문자열.
function buildSummary(f: FormState): string {
  const won = (n: number) => (n ?? 0).toLocaleString('ko-KR') + '원';
  const catMap: Record<string, string> = { repay: '반환', normal: '일반', bad: '악성' };
  const cat = catMap[f.category] ?? f.category;
  const method = f.payout_method === 'wallet' ? '지갑정산' : '은행정산';
  const acct = f.payout_method === 'wallet'
    ? (f.people_id ? `피플 ID: ${f.people_id}` : '피플 ID: (미입력)')
    : (f.payout_bank ? `${f.payout_bank} ${f.payout_account || '-'} (${f.payout_holder || '-'})` : '(정산 계좌 미입력)');
  return [
    `가맹점명: ${f.name}`,
    `MID: ${f.mid}`,
    `고객분류: ${cat}`,
    `시작일: ${f.start_date}  ·  기간: ${f.term_days}일`,
    `매입액: ${won(f.purchase_amount)}  ·  입금액: ${won(f.deposit_amount)}`,
    `수수료율: ${f.fee_rate}%`,
    `정산방식: ${method}`,
    `정산 수단: ${acct}`,
    `반환 계좌: ${f.repay_bank || '-'} ${f.repay_account || '-'} (${f.repay_holder || '-'})`,
    f.ceo_name || f.contact_phone || f.business_no ? '' : null,
    f.ceo_name ? `대표자명: ${f.ceo_name}` : null,
    f.business_no ? `사업자등록번호: ${f.business_no}` : null,
    f.contact_phone ? `연락처: ${f.contact_phone}` : null,
  ].filter(Boolean).join('\n');
}

export default function MerchantForm() {
  const { mid: routeMid } = useParams();
  const isEdit = !!routeMid;
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(empty);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [fieldErrs, setFieldErrs] = useState<FormErrors>({});
  const [midConflict, setMidConflict] = useState<{ mid: string } | null>(null);
  // 결제내역 요약 (수정 모드 · 자동 생성 가맹점 참고용)
  type TxSummary = {
    tx_count: number; paid_count: number; cancel_count: number;
    paid_gross: number; cancel_gross: number; net_gross: number;
    first_trx_date: string | null; last_trx_date: string | null;
  };
  const [txSummary, setTxSummary] = useState<TxSummary | null>(null);
  // MID 변경 모달
  const [midChangeOpen, setMidChangeOpen] = useState(false);
  const [midChangeValue, setMidChangeValue] = useState('');
  const [midChanging, setMidChanging] = useState(false);

  // 소송·관리 정보 (edit 모드 전용)
  type LitigationForm = {
    status: 'none' | 'in_progress' | 'ended';
    litigation_type: 'payment_order' | 'civil_suit' | 'other' | '';
    last_action_date: string;
    has_cost: boolean;
    cumulative_cost: number;
    memo: string;
  };
  type LitigationServer = {
    status: 'none' | 'in_progress' | 'ended';
    litigation_type: string | null;
    last_action_date: string | null;
    has_cost: number;
    cumulative_cost: number;
    memo: string | null;
    updated_at: string | null;
    updated_by: string | null;
  };
  type LitigationHistRow = LitigationServer & { id: number; changed_at: string; changed_by: string | null };
  const emptyLit: LitigationForm = {
    status: 'none', litigation_type: '', last_action_date: '',
    has_cost: false, cumulative_cost: 0, memo: '',
  };
  const [lit, setLit] = useState<LitigationForm>(emptyLit);
  const [litMeta, setLitMeta] = useState<{ updated_at: string | null; updated_by: string | null }>({ updated_at: null, updated_by: null });
  const [litSaving, setLitSaving] = useState(false);
  const [litHistory, setLitHistory] = useState<LitigationHistRow[]>([]);
  const [litHistoryOpen, setLitHistoryOpen] = useState(false);

  // 조정금액 (append-only 이력 + 등록 폼)
  type AdjustmentRow = {
    id: number;
    kind: 'remaining' | 'return_daily' | 'device_fee' | 'shortfall' | 'sale_cancel';
    effective_date: string;
    amount: number;
    reason: string | null;
    period_yyyymm: string | null;
    created_at: string;
    created_by: string | null;
  };
  type AdjAttachment = { id: number; filename: string; mime: string; byte_size: number; uploaded_at: string; uploaded_by: string | null };
  const ADJ_KIND_KO: Record<string, string> = {
    remaining: '잔여액 조정 (소송비용/협의비용)',
    return_daily: '당일 반환 조정',
    shortfall: '전일미차감 조정 (부분 반환·통신비 누락 등)',
    sale_cancel: '매출취소 조정 (지정 정산일에만 1회 반영)',
    device_fee: '단말기 통신비 (자동)',
  };
  const [adjustments, setAdjustments] = useState<AdjustmentRow[]>([]);
  const [adjSums, setAdjSums] = useState<{ remaining_sum: number; return_today_sum: number; shortfall_sum: number }>({ remaining_sum: 0, return_today_sum: 0, shortfall_sum: 0 });
  const [adjSaving, setAdjSaving] = useState(false);
  const [adjKind, setAdjKind] = useState<'remaining' | 'return_daily' | 'shortfall' | 'sale_cancel'>('remaining');
  const [adjDate, setAdjDate] = useState<string>(() => {
    const kst = new Date(Date.now() + 9 * 3600 * 1000);
    return kst.toISOString().slice(0, 10);
  });
  const [adjAmount, setAdjAmount] = useState<number>(0);
  const [adjReason, setAdjReason] = useState<string>('');
  const [adjPendingFile, setAdjPendingFile] = useState<File | null>(null);
  const [adjAttOpen, setAdjAttOpen] = useState<Set<number>>(new Set());
  const [adjAttMap, setAdjAttMap] = useState<Record<number, AdjAttachment[]>>({});
  const [hasDevice, setHasDevice] = useState<boolean>(false);
  const [hasDeviceSaving, setHasDeviceSaving] = useState(false);
  // 단말기 반납 상태 (문서4 §5)
  const [deviceReturnStatus, setDeviceReturnStatus] = useState<'unreturned' | 'returned' | 'not_applicable'>('unreturned');
  const [deviceReturnDate, setDeviceReturnDate] = useState<string>('');
  const [deviceReturnSaving, setDeviceReturnSaving] = useState(false);

  const loadAdjustments = async (mid: string) => {
    try {
      const r = await api<{ data: AdjustmentRow[]; sums: { remaining_sum: number; return_today_sum: number; shortfall_sum: number } }>(
        'GET', `/merchants/${encodeURIComponent(mid)}/adjustments`
      );
      setAdjustments(r.data ?? []);
      setAdjSums(r.sums ?? { remaining_sum: 0, return_today_sum: 0, shortfall_sum: 0 });
    } catch { setAdjustments([]); }
  };
  const saveAdjustment = async () => {
    if (!routeMid) return;
    const amt = Math.round(adjAmount);
    if (!Number.isFinite(amt) || amt === 0) { message.error('금액은 0이 아닌 정수여야 합니다.'); return; }
    setAdjSaving(true);
    try {
      const created = await api<{ ok: true; id: number }>(
        'POST', `/merchants/${encodeURIComponent(routeMid)}/adjustments`,
        { kind: adjKind, effective_date: adjDate, amount: amt, reason: adjReason || null }
      );
      // 첨부가 선택되었으면 후속 업로드 (5MB, JPG/PNG)
      if (adjPendingFile && created?.id) {
        try {
          const fd = new FormData();
          fd.append('file', adjPendingFile);
          await api('POST', `/merchants/${encodeURIComponent(routeMid)}/adjustments/${created.id}/attachments`, fd);
        } catch (e: any) {
          message.warning(`조정은 등록됐지만 첨부 실패: ${e?.message ?? '오류'}`);
        }
      }
      message.success('조정 등록됨');
      setAdjAmount(0);
      setAdjReason('');
      setAdjPendingFile(null);
      await loadAdjustments(routeMid);
    } catch (e: any) {
      message.error(e?.message ?? '저장 실패');
    } finally { setAdjSaving(false); }
  };
  const toggleAdjAtt = async (aid: number) => {
    if (!routeMid) return;
    if (adjAttOpen.has(aid)) {
      setAdjAttOpen(prev => { const s = new Set(prev); s.delete(aid); return s; });
      return;
    }
    if (!adjAttMap[aid]) {
      try {
        const r = await api<{ data: AdjAttachment[] }>(
          'GET', `/merchants/${encodeURIComponent(routeMid)}/adjustments/${aid}/attachments`
        );
        setAdjAttMap(prev => ({ ...prev, [aid]: r.data ?? [] }));
      } catch { setAdjAttMap(prev => ({ ...prev, [aid]: [] })); }
    }
    setAdjAttOpen(prev => { const s = new Set(prev); s.add(aid); return s; });
  };
  const deleteAdjAtt = async (aid: number, attId: number) => {
    if (!routeMid) return;
    if (!confirm('이 증빙 파일을 삭제하시겠습니까?')) return;
    try {
      await api('DELETE', `/merchants/${encodeURIComponent(routeMid)}/adjustments/${aid}/attachments/${attId}`);
      setAdjAttMap(prev => ({ ...prev, [aid]: (prev[aid] ?? []).filter(a => a.id !== attId) }));
    } catch (e: any) { message.error(e?.message ?? '삭제 실패'); }
  };
  const reverseAdjustment = async (id: number) => {
    if (!routeMid) return;
    if (!confirm('이 조정을 반대 부호로 반전 등록합니다 (감사 이력 보존을 위해 원본은 유지). 계속하시겠습니까?')) return;
    try {
      await api('POST', `/merchants/${encodeURIComponent(routeMid)}/adjustments/${id}/reverse`, {});
      message.success('반전 등록됨');
      await loadAdjustments(routeMid);
    } catch (e: any) {
      message.error(e?.message ?? '반전 실패');
    }
  };
  const saveDeviceReturn = async () => {
    if (!routeMid) return;
    setDeviceReturnSaving(true);
    try {
      await api('PATCH', `/merchants/${encodeURIComponent(routeMid)}/device-return`, {
        status: deviceReturnStatus,
        return_date: deviceReturnStatus === 'returned' ? (deviceReturnDate || null) : null,
      });
      message.success('단말기 반납 상태 저장됨');
    } catch (e: any) {
      message.error(e?.message ?? '저장 실패');
    } finally { setDeviceReturnSaving(false); }
  };
  const toggleHasDevice = async () => {
    if (!routeMid) return;
    const next = !hasDevice;
    setHasDeviceSaving(true);
    try {
      await api('PATCH', `/merchants/${encodeURIComponent(routeMid)}/device`, { has_device: next });
      setHasDevice(next);
      message.success(next ? '단말기 사용 대상 등록' : '단말기 사용 대상 해제');
    } catch (e: any) {
      message.error(e?.message ?? '변경 실패');
    } finally { setHasDeviceSaving(false); }
  };

  const loadLitigation = async (mid: string) => {
    try {
      const r = await api<{ data: LitigationServer }>('GET', `/merchants/${encodeURIComponent(mid)}/litigation`);
      const d = r.data;
      setLit({
        status: d.status ?? 'none',
        litigation_type: (d.litigation_type as any) ?? '',
        last_action_date: d.last_action_date ?? '',
        has_cost: !!d.has_cost,
        cumulative_cost: Number(d.cumulative_cost ?? 0),
        memo: d.memo ?? '',
      });
      setLitMeta({ updated_at: d.updated_at ?? null, updated_by: d.updated_by ?? null });
    } catch { /* ignore — 미등록 상태로 처리 */ }
  };
  const loadLitigationHistory = async (mid: string) => {
    try {
      const r = await api<{ data: LitigationHistRow[] }>('GET', `/merchants/${encodeURIComponent(mid)}/litigation/history`);
      setLitHistory(r.data ?? []);
    } catch { setLitHistory([]); }
  };
  const saveLitigation = async () => {
    if (!routeMid) return;
    setLitSaving(true);
    try {
      await api('PUT', `/merchants/${encodeURIComponent(routeMid)}/litigation`, {
        status: lit.status,
        litigation_type: lit.litigation_type || null,
        last_action_date: lit.last_action_date || null,
        has_cost: lit.has_cost ? 1 : 0,
        cumulative_cost: Math.max(0, Math.round(lit.cumulative_cost || 0)),
        memo: lit.memo || null,
      });
      message.success('소송·관리 정보 저장됨');
      await loadLitigation(routeMid);
      if (litHistoryOpen) await loadLitigationHistory(routeMid);
    } catch (e: any) {
      message.error(e?.message ?? '저장 실패');
    } finally { setLitSaving(false); }
  };

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const r = await api<{ data: any }>('GET', `/merchants/${encodeURIComponent(routeMid!)}`);
        const d = r.data;
        // 수정 모드: DB 값 우선. 단, repay 계좌가 비어있으면 LocalStorage 기본값으로 자동 채움.
        const def = loadRepayDefault();
        setForm({
          mid: d.mid, name: d.name, start_date: (d.start_date ?? '').slice(0, 10),
          term_days: d.term_days, deposit_amount: d.deposit_amount, purchase_amount: d.purchase_amount,
          purchase_not_applicable: !!d.purchase_not_applicable,
          fee_rate: Number(((d.fee_rate ?? 0) * 100).toFixed(4)),
          category: d.category,
          payout_method: (d.payout_method ?? 'bank') as 'bank' | 'wallet',
          people_id: d.people_id ?? '',
          payout_bank: d.payout_bank ?? '', payout_account: d.payout_account ?? '',
          payout_holder: d.payout_holder ?? '', payout_memo: d.payout_memo ?? '',
          repay_bank:    d.repay_bank    || def.bank,
          repay_account: d.repay_account || def.account,
          repay_holder:  d.repay_holder  || def.holder,
          repay_memo:    d.repay_memo    ?? '',
          ceo_name:         d.ceo_name ?? '',
          contact_phone:    d.contact_phone ?? '',
          business_no:      d.business_no ?? '',
          business_address: d.business_address ?? '',
        });
        // 결제내역 요약 병행 조회 (실패해도 폼 렌더에 영향 없게)
        try {
          const t = await api<{ data: TxSummary | null }>('GET', `/merchants/${encodeURIComponent(routeMid!)}/tx-summary`);
          setTxSummary(t.data);
        } catch { setTxSummary(null); }
        // 소송·관리 정보 병행 조회
        await loadLitigation(routeMid!);
        // 조정 이력 병행 조회
        await loadAdjustments(routeMid!);
        // 단말기 사용 여부 반영
        setHasDevice(!!d.has_device);
        setDeviceReturnStatus((d.device_return_status as any) ?? 'unreturned');
        setDeviceReturnDate(d.device_return_date ?? '');
      } catch (e: any) {
        setErr(e?.message ?? '로드 실패');
      } finally { setLoading(false); }
    })();
  }, [isEdit, routeMid]);

  // MID 변경 실행 — Merchants.tsx의 renameMid와 동일 정책 (관련 거래·정산·이체 mcht_id 자동 갱신).
  const openMidChange = () => {
    setMidChangeValue(form.mid);
    setMidChangeOpen(true);
  };
  const doMidChange = async () => {
    const newMid = midChangeValue.trim();
    if (!newMid || newMid === form.mid) { setMidChangeOpen(false); return; }
    if (!/^[a-zA-Z0-9._-]+$/.test(newMid)) {
      Modal.warning({ title: 'MID 형식 오류', content: 'MID는 영문/숫자/._- 만 허용됩니다.', okText: '확인' });
      return;
    }
    setMidChanging(true);
    try {
      await api('PATCH', `/merchants/${encodeURIComponent(form.mid)}/rename`, { new_mid: newMid });
      const oldMid = form.mid;
      setMidChangeOpen(false);
      message.success(`MID 변경 완료: ${oldMid} → ${newMid}`);
      // 새 URL로 이동 (읽기 갱신 위해)
      navigate(`/merchants/${encodeURIComponent(newMid)}`, { replace: true });
    } catch (e: any) {
      if (e?.message === 'new_mid_exists') {
        Modal.error({
          title: 'MID 변경 실패',
          content: '새 MID로 이미 등록된 가맹점이 있습니다. 그 가맹점을 먼저 삭제하거나 흡수해야 합니다.',
          okText: '확인',
        });
      } else {
        Modal.error({ title: 'MID 변경 실패', content: e?.message ?? '오류', okText: '확인' });
      }
    } finally { setMidChanging(false); }
  };

  const update = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm(f => {
    const next = { ...f, [k]: v };
    setFieldErrs(validateForm(next));
    return next;
  });

  // MID 신규 입력 시 blur에 중복 조회 — 이미 정식 등록된 MID면 즉시 안내.
  const checkMidConflict = async () => {
    if (isEdit) return;
    const mid = form.mid.trim();
    if (!mid || !/^[a-zA-Z0-9._-]+$/.test(mid)) return;
    try {
      const r = await api<{ data: any }>('GET', `/merchants/${encodeURIComponent(mid)}`);
      if (r?.data && r.data.active === 1) setMidConflict({ mid });
      else setMidConflict(null);
    } catch {
      setMidConflict(null);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    const errs = validateForm(form);
    setFieldErrs(errs);
    if (Object.keys(errs).length > 0) {
      setErr('입력값을 확인해 주세요. 오류 항목을 수정 후 다시 저장하세요.');
      return;
    }
    // 저장 전 최종 확인 — 요약 표시.
    const summary = buildSummary(form);
    const title = isEdit ? '아래 정보로 가맹점을 수정합니다.' : '아래 정보로 신규 가맹점을 등록합니다.';
    if (!confirm(`${title}\n\n${summary}\n\n확인 후 [확인]을 눌러 진행합니다.`)) return;

    setSaving(true);
    try {
      const payload = {
        ...form,
        fee_rate: Number((form.fee_rate / 100).toFixed(6)),  // 4.4(%) → 0.044
        people_id: form.people_id.trim() || null,
        payout_memo: form.payout_memo || `${form.name} 정산`,
        repay_memo: form.repay_memo || `${form.name} 반환`,
      };
      if (isEdit) await api('PUT', `/merchants/${encodeURIComponent(routeMid!)}`, payload);
      else await api('POST', '/merchants', payload);
      if (form.repay_bank && form.repay_account && form.repay_holder) {
        saveRepayDefault({
          bank: form.repay_bank, account: form.repay_account, holder: form.repay_holder,
        });
      }
      // 저장 완료 안내 — 신규 등록 시 자동 활성화 여부 표시.
      alert(isEdit
        ? `저장이 완료되었습니다.`
        : `등록이 완료되었습니다.\n\n${form.name} 계정은 즉시 활성화되어 정산 화면에 노출됩니다.`);
      navigate('/merchants');
    } catch (e: any) {
      const msg = e?.body?.message ?? e?.message ?? '저장 실패';
      setErr(msg === 'already_exists' ? '이미 정식 등록된 MID입니다. 수정 화면을 이용해 주세요.' : msg);
    } finally { setSaving(false); }
  };

  if (loading) return <p>로딩…</p>;

  return (
    <div>
      <h2>{isEdit ? `가맹점 수정 — ${form.name}` : '신규 가맹점 등록'}</h2>

      {/* 결제내역 요약 — 수정 모드에서 자동 생성 가맹점 판단·정보 참고. */}
      {isEdit && txSummary && txSummary.tx_count > 0 && (
        <div className="card" style={{ marginBottom: 12, padding: '12px 16px', background: '#f8fafc', border: '1px solid var(--border)' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 8 }}>
            📊 기존 결제내역 참고 <span style={{ fontWeight: 400 }} title="기술 필드명: is_test=0">(운영 기준)</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'baseline' }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>누적 거래</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--leaf-800)' }}>
                {txSummary.tx_count.toLocaleString('ko-KR')}건
                <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500, marginLeft: 6 }}>
                  승인 {txSummary.paid_count} · 취소 {txSummary.cancel_count}
                </span>
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>누적 매출 (순매출)</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--leaf-800)' }}>{fmt.won(txSummary.net_gross)}원</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>승인 {fmt.won(txSummary.paid_gross)} · 취소 {fmt.won(txSummary.cancel_gross)}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>최초 거래일</div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{txSummary.first_trx_date ?? '-'}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>최근 거래일</div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{txSummary.last_trx_date ?? '-'}</div>
            </div>
            <div style={{ marginLeft: 'auto' }}>
              <a href={`/notifications?mcht_id=${encodeURIComponent(form.mid)}`}
                 onClick={(e) => { e.preventDefault(); navigate(`/notifications?mcht_id=${encodeURIComponent(form.mid)}`); }}
                 style={{ fontSize: 12, textDecoration: 'underline', cursor: 'pointer', color: 'var(--brand-700)' }}>
                결제내역 전체 보기 →
              </a>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="card">
        <div style={{ marginBottom: 8, fontSize: 12, color: 'var(--text-muted)' }}>
          <Req /> 표시된 항목은 필수 입력입니다.
        </div>
        <div className="form-grid">
          <div>
            <label>
              MID<Req />
              {isEdit && (
                <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-muted)' }}>
                  (읽기 전용 — 결제정보 연결 기준값)
                </span>
              )}
            </label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <input value={form.mid}
                     onChange={e => update('mid', e.target.value)}
                     onBlur={checkMidConflict}
                     required disabled={isEdit}
                     style={{ flex: '1 1 140px', minWidth: 0 }}
                     title={isEdit ? 'MID는 결제 원장·정산 스냅샷 연결의 기준값이라 일반 수정에서는 잠금. [MID 변경/연결]로만 변경 가능.' : undefined} />
              {isEdit && (
                <button type="button" onClick={openMidChange}
                        style={{
                          padding: '6px 12px', background: '#fff', color: 'var(--brand-700)',
                          border: '1px solid var(--brand-300, #a7f3d0)', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          flex: '0 0 auto',
                          minWidth: 120,
                        }}>
                  MID 변경/연결
                </button>
              )}
            </div>
            {fieldErrs.mid && <div style={{ color: '#b91c1c', fontSize: 11, marginTop: 3 }}>{fieldErrs.mid}</div>}
            {midConflict && !isEdit && (
              <div style={{ color: '#b91c1c', fontSize: 11, marginTop: 3, display: 'flex', gap: 6 }}>
                이미 등록된 MID입니다.
                <a href={`/merchants/${encodeURIComponent(midConflict.mid)}`}
                   onClick={(e) => { e.preventDefault(); navigate(`/merchants/${encodeURIComponent(midConflict.mid)}`); }}
                   style={{ textDecoration: 'underline', cursor: 'pointer' }}>
                  기존 가맹점 보기
                </a>
              </div>
            )}
          </div>
          <div>
            <label>가맹점명<Req /></label>
            <input value={form.name} onChange={e => update('name', e.target.value)} required style={{ width: '100%' }} />
            {fieldErrs.name && <div style={{ color: '#b91c1c', fontSize: 11, marginTop: 3 }}>{fieldErrs.name}</div>}
          </div>
          <div>
            <label>시작일<Req /></label>
            <input type="date" value={form.start_date} onChange={e => update('start_date', e.target.value)} required style={{ width: '100%' }} />
            {fieldErrs.start_date && <div style={{ color: '#b91c1c', fontSize: 11, marginTop: 3 }}>{fieldErrs.start_date}</div>}
          </div>
          <div>
            <label>기간 (일){form.category === 'repay' && form.purchase_amount > 0 && <Req />}</label>
            <input type="number" value={form.term_days} onChange={e => update('term_days', Number(e.target.value))} min={0} style={{ width: '100%' }} />
            {fieldErrs.term_days && <div style={{ color: '#b91c1c', fontSize: 11, marginTop: 3 }}>{fieldErrs.term_days}</div>}
          </div>
          <div>
            <label>입금액 (원)</label>
            <MoneyInput value={form.deposit_amount} onChange={v => update('deposit_amount', v)} style={{ width: '100%' }} />
            {fieldErrs.deposit_amount && <div style={{ color: '#b91c1c', fontSize: 11, marginTop: 3 }}>{fieldErrs.deposit_amount}</div>}
          </div>
          <div>
            <label>매입액 (원){form.category === 'repay' && !form.purchase_not_applicable && <Req />}</label>
            <MoneyInput value={form.purchase_amount}
                        onChange={v => update('purchase_amount', v)}
                        style={{ width: '100%' }} />
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 12, color: 'var(--text-muted)' }}>
              <input type="checkbox" checked={form.purchase_not_applicable}
                     onChange={e => update('purchase_not_applicable', e.target.checked)} />
              <span>매입액 미해당 (매입 계약 없음 — 정산만 진행)</span>
            </label>
            {fieldErrs.purchase_amount && <div style={{ color: '#b91c1c', fontSize: 11, marginTop: 3 }}>{fieldErrs.purchase_amount}</div>}
          </div>
          <div>
            <label>수수료율 (%)<Req /></label>
            <div style={{ position: 'relative' }}>
              <input type="number" step="0.01" value={form.fee_rate}
                     onChange={e => update('fee_rate', Number(e.target.value))}
                     required min={0} max={100}
                     style={{ width: '100%', paddingRight: 28 }} placeholder="예: 4.4" />
              <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)', fontSize: 13, pointerEvents: 'none' }}>%</span>
            </div>
            {fieldErrs.fee_rate && <div style={{ color: '#b91c1c', fontSize: 11, marginTop: 3 }}>{fieldErrs.fee_rate}</div>}
          </div>
          <div><label>고객분류<Req /></label>
            <select value={form.category} onChange={e => update('category', e.target.value as any)} style={{ width: '100%' }}>
              {CATEGORY_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.k} — {o.desc}</option>)}
            </select>
          </div>
          <div><label>정산방식<Req /></label>
            <select value={form.payout_method}
                    onChange={e => update('payout_method', e.target.value as 'bank' | 'wallet')}
                    style={{ width: '100%' }}>
              <option value="bank">은행정산 — 계좌로 송금</option>
              <option value="wallet">지갑정산 — 피플 ID로 송금</option>
            </select>
          </div>
          {form.payout_method === 'wallet' && (
            <div>
              <label>가맹점 피플 ID<Req /></label>
              <input value={form.people_id} onChange={e => update('people_id', e.target.value)}
                     placeholder="예: people12345" style={{ width: '100%' }} />
              {fieldErrs.people_id && <div style={{ color: '#b91c1c', fontSize: 11, marginTop: 3 }}>{fieldErrs.people_id}</div>}
            </div>
          )}
        </div>

        <fieldset style={{ marginTop: 18 }}>
          <legend>사업장 정보 (선택)</legend>
          <div className="form-grid">
            <div><label>대표자명</label>
              <input value={form.ceo_name} onChange={e => update('ceo_name', e.target.value)} style={{ width: '100%' }} placeholder="예: 홍길동" />
            </div>
            <div><label>연락처</label>
              <input value={form.contact_phone} onChange={e => update('contact_phone', e.target.value)} style={{ width: '100%' }} placeholder="예: 010-1234-5678" />
            </div>
            <div><label>사업장 번호 (사업자등록번호)</label>
              <input value={form.business_no} onChange={e => update('business_no', e.target.value)} style={{ width: '100%' }} placeholder="예: 123-45-67890" />
              {fieldErrs.business_no && <div style={{ color: '#b91c1c', fontSize: 11, marginTop: 3 }}>{fieldErrs.business_no}</div>}
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label>사업장 주소지</label>
              <input value={form.business_address} onChange={e => update('business_address', e.target.value)} style={{ width: '100%' }} placeholder="예: 서울시 강남구 ..." />
            </div>
          </div>
        </fieldset>

        {form.payout_method === 'bank' && (
          <fieldset style={{ marginTop: 14 }}>
            <legend>정산 계좌 — 가맹점 정산금 입금 (회사 → 가맹점)</legend>
            <div className="form-grid">
              <div><label>은행</label><input value={form.payout_bank} onChange={e => update('payout_bank', e.target.value)} style={{ width: '100%' }} /></div>
              <div><label>계좌번호</label><input value={form.payout_account} onChange={e => update('payout_account', e.target.value)} style={{ width: '100%' }} /></div>
              <div><label>예금주</label><input value={form.payout_holder} onChange={e => update('payout_holder', e.target.value)} style={{ width: '100%' }} /></div>
              <div><label>입금 통장 표시</label><input value={form.payout_memo} onChange={e => update('payout_memo', e.target.value)} placeholder={`${form.name || '...'} 정산`} style={{ width: '100%' }} /></div>
            </div>
          </fieldset>
        )}

        <fieldset style={{ marginTop: 14 }}>
          <legend>반환 계좌 — 원금 반환 입금 (가맹점 → 회사)</legend>
          <div className="form-grid">
            <div><label>은행</label><input value={form.repay_bank} onChange={e => update('repay_bank', e.target.value)} style={{ width: '100%' }} /></div>
            <div><label>계좌번호</label><input value={form.repay_account} onChange={e => update('repay_account', e.target.value)} style={{ width: '100%' }} /></div>
            <div><label>예금주</label><input value={form.repay_holder} onChange={e => update('repay_holder', e.target.value)} style={{ width: '100%' }} /></div>
            <div><label>입금 통장 표시</label><input value={form.repay_memo} onChange={e => update('repay_memo', e.target.value)} placeholder={`${form.name || '...'} 반환`} style={{ width: '100%' }} /></div>
          </div>
        </fieldset>

        {err && <div className="error">{err}</div>}
        <div style={{ marginTop: 18, display: 'flex', gap: 8 }}>
          <button className="btn" disabled={saving} type="submit">{saving ? '저장 중…' : '저장'}</button>
          <button className="btn ghost" type="button" onClick={() => navigate('/merchants')}>취소</button>
        </div>
      </form>

      {isEdit && (
        <fieldset style={{ marginTop: 18 }}>
          <legend>소송·관리 정보</legend>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
            무매출·장기 미납 가맹점의 소송 진행 상태와 비용을 관리합니다. 저장한 정보는 무매출 알림에서 조회·필터에 활용됩니다.
            {litMeta.updated_at && (
              <span style={{ marginLeft: 8 }}>
                · 최종 저장 <b style={{ fontFamily: 'monospace' }}>{litMeta.updated_at}</b>
                {litMeta.updated_by ? ` (${litMeta.updated_by})` : ''}
              </span>
            )}
          </div>
          <div className="form-grid">
            <div>
              <label>소송 진행 여부</label>
              <select value={lit.status}
                      onChange={e => setLit(v => ({ ...v, status: e.target.value as any }))}
                      style={{ width: '100%' }}>
                <option value="none">없음</option>
                <option value="in_progress">진행 중</option>
                <option value="ended">종료</option>
              </select>
            </div>
            <div>
              <label>진행 유형</label>
              <select value={lit.litigation_type}
                      onChange={e => setLit(v => ({ ...v, litigation_type: e.target.value as any }))}
                      style={{ width: '100%' }}
                      disabled={lit.status === 'none'}>
                <option value="">(미분류)</option>
                <option value="payment_order">지급명령</option>
                <option value="civil_suit">민사소송</option>
                <option value="other">기타</option>
              </select>
            </div>
            <div>
              <label>최근 진행일</label>
              <input type="date" value={lit.last_action_date}
                     onChange={e => setLit(v => ({ ...v, last_action_date: e.target.value }))}
                     disabled={lit.status === 'none'}
                     style={{ width: '100%' }} />
            </div>
            <div>
              <label>소송비용 발생 여부</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <input type="checkbox" checked={lit.has_cost}
                         onChange={e => setLit(v => ({ ...v, has_cost: e.target.checked }))} />
                  <span>비용 발생</span>
                </label>
              </div>
            </div>
            <div>
              <label>누적 소송비용 (원)</label>
              <MoneyInput value={lit.cumulative_cost}
                          onChange={n => setLit(v => ({ ...v, cumulative_cost: n }))}
                          placeholder="예: 67900"
                          style={{ width: '100%' }} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label>관리 메모</label>
              <textarea value={lit.memo}
                        onChange={e => setLit(v => ({ ...v, memo: e.target.value }))}
                        rows={3}
                        placeholder="예: 2026-08-20 지급명령 접수, 8/25 응답 예정"
                        maxLength={2000}
                        style={{ width: '100%', fontFamily: 'inherit', fontSize: 13, resize: 'vertical' }} />
            </div>
          </div>
          <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
            <button type="button" className="btn" onClick={saveLitigation} disabled={litSaving}>
              {litSaving ? '저장 중…' : '소송·관리 정보 저장'}
            </button>
            <button type="button" className="btn ghost"
                    onClick={async () => {
                      const willOpen = !litHistoryOpen;
                      setLitHistoryOpen(willOpen);
                      if (willOpen && routeMid) await loadLitigationHistory(routeMid);
                    }}>
              {litHistoryOpen ? '이력 접기' : `변경 이력 (${litHistory.length || '조회'})`}
            </button>
          </div>
          {litHistoryOpen && (
            <div className="table-wrap" style={{ marginTop: 10, maxHeight: 260, border: '1px solid var(--border, #e6ece9)', borderRadius: 6 }}>
              <table>
                <thead><tr>
                  <th className="nowrap">저장 시각 (KST)</th>
                  <th className="nowrap">처리자</th>
                  <th className="nowrap">상태</th>
                  <th className="nowrap">유형</th>
                  <th className="nowrap">최근 진행일</th>
                  <th className="nowrap">비용</th>
                  <th className="num nowrap">누적 비용</th>
                  <th>메모</th>
                </tr></thead>
                <tbody>
                  {litHistory.length === 0 ? (
                    <tr><td colSpan={8} style={{ padding: 12, color: 'var(--text-muted)' }}>변경 이력 없음</td></tr>
                  ) : litHistory.map(h => (
                    <tr key={h.id}>
                      <td className="nowrap cell-mono">{h.changed_at}</td>
                      <td className="nowrap cell-mono">{h.changed_by ?? '-'}</td>
                      <td className="nowrap">{
                        h.status === 'in_progress' ? '진행 중' :
                        h.status === 'ended' ? '종료' : '없음'
                      }</td>
                      <td className="nowrap">{
                        h.litigation_type === 'payment_order' ? '지급명령' :
                        h.litigation_type === 'civil_suit' ? '민사소송' :
                        h.litigation_type === 'other' ? '기타' : '-'
                      }</td>
                      <td className="nowrap cell-mono">{h.last_action_date ?? '-'}</td>
                      <td className="nowrap">{h.has_cost ? '발생' : '없음'}</td>
                      <td className="num nowrap">{fmt.won(h.cumulative_cost)}원</td>
                      <td style={{ fontSize: 12 }}>{h.memo ?? '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </fieldset>
      )}

      {isEdit && (
        <fieldset style={{ marginTop: 18 }}>
          <legend>정산 조정 (조정금액 · 반환 조정)</legend>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10, lineHeight: 1.6 }}>
            <b>잔여액 조정</b>은 소송비용·협의비용 등 실제 정산금액에 반영해야 하는 금액입니다.
            양수(+)는 잔여액 증가(회수해야 할 금액 증가), 음수(−)는 감액.
            <br/>
            <b>당일 반환 조정</b>은 당일 반환금액을 별도로 가감합니다. 조정한 만큼 당일 잔여액은 반대 방향으로 자동 연동됩니다.
            <br/>
            등록 후 삭제는 불가능하며, 취소하려면 반대 부호로 반전 등록합니다 (감사 이력 보존).
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8, padding: 10, background: 'var(--surface-alt, #fafbfa)', border: '1px solid var(--border, #e6ece9)', borderRadius: 6, marginBottom: 10 }}>
            <div style={{ fontSize: 12 }}>
              <div style={{ color: 'var(--text-muted)' }}>잔여액 조정 누적</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: adjSums.remaining_sum >= 0 ? '#c2410c' : 'var(--leaf-700)' }}>
                {adjSums.remaining_sum >= 0 ? '+' : ''}{fmt.won(adjSums.remaining_sum)}원
              </div>
              <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>양수 = 잔여액 가산 (부채 증가)</div>
            </div>
            <div style={{ fontSize: 12 }}>
              <div style={{ color: 'var(--text-muted)' }}>오늘 반환 조정</div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>
                {adjSums.return_today_sum >= 0 ? '+' : ''}{fmt.won(adjSums.return_today_sum)}원
              </div>
              <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>반환액 가감 = 잔여 반대 조정</div>
            </div>
            <div style={{ fontSize: 12 }}>
              <div style={{ color: 'var(--text-muted)' }}>전일미차감 조정 누적</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: adjSums.shortfall_sum >= 0 ? '#c2410c' : 'var(--leaf-700)' }}>
                {adjSums.shortfall_sum >= 0 ? '+' : ''}{fmt.won(adjSums.shortfall_sum)}원
              </div>
              <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>양수 = 미차감 가산 (예: 통신비)</div>
            </div>
          </div>

          <div className="form-grid">
            <div>
              <label>조정 종류</label>
              <select value={adjKind} onChange={e => setAdjKind(e.target.value as any)} style={{ width: '100%' }}>
                <option value="remaining">{ADJ_KIND_KO.remaining}</option>
                <option value="return_daily">{ADJ_KIND_KO.return_daily}</option>
                <option value="shortfall">{ADJ_KIND_KO.shortfall}</option>
                <option value="sale_cancel">{ADJ_KIND_KO.sale_cancel}</option>
              </select>
            </div>
            <div>
              <label>반영 기준일</label>
              <input type="date" value={adjDate} onChange={e => setAdjDate(e.target.value)} style={{ width: '100%' }} />
            </div>
            <div>
              <label>금액 (원, +/−)</label>
              <input type="number" value={adjAmount || ''}
                     onChange={e => setAdjAmount(Number(e.target.value) || 0)}
                     placeholder="예: 67900 또는 -10000"
                     style={{ width: '100%' }} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label>사유</label>
              <input value={adjReason} onChange={e => setAdjReason(e.target.value)}
                     placeholder="예: 2026-08-20 지급명령 인지대 부과"
                     maxLength={500}
                     style={{ width: '100%' }} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label>증빙 이미지 첨부 (선택 · JPG/PNG · 최대 5MB · 조정당 3장까지)</label>
              <input type="file" accept="image/jpeg,image/png"
                     onChange={e => {
                       const f = e.target.files?.[0] ?? null;
                       if (f && f.size > 5 * 1024 * 1024) { message.error('파일 크기 5MB 초과'); e.target.value = ''; return; }
                       setAdjPendingFile(f);
                     }} />
              {adjPendingFile && (
                <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-muted)' }}>
                  선택됨: <b>{adjPendingFile.name}</b> ({(adjPendingFile.size / 1024).toFixed(0)}KB)
                  <button type="button" onClick={() => setAdjPendingFile(null)}
                          className="filter-ghost" style={{ marginLeft: 8, padding: '2px 8px', fontSize: 11 }}>제거</button>
                </div>
              )}
            </div>
          </div>
          <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
            <button type="button" className="btn" onClick={saveAdjustment} disabled={adjSaving}>
              {adjSaving ? '등록 중…' : '조정 등록'}
            </button>
          </div>

          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>조정 이력 ({adjustments.length}건)</div>
            <div className="table-wrap" style={{ maxHeight: 280, border: '1px solid var(--border, #e6ece9)', borderRadius: 6 }}>
              <table>
                <thead><tr>
                  <th className="nowrap">기준일</th>
                  <th className="nowrap">종류</th>
                  <th className="num nowrap">금액</th>
                  <th>사유</th>
                  <th className="nowrap">등록자</th>
                  <th className="nowrap">등록 시각</th>
                  <th></th>
                </tr></thead>
                <tbody>
                  {adjustments.length === 0 ? (
                    <tr><td colSpan={7} style={{ padding: 12, color: 'var(--text-muted)' }}>조정 이력 없음</td></tr>
                  ) : adjustments.map(a => {
                    const attOpen = adjAttOpen.has(a.id);
                    const atts = adjAttMap[a.id];
                    return (
                      <React.Fragment key={a.id}>
                        <tr>
                          <td className="nowrap cell-mono">{a.effective_date}</td>
                          <td className="nowrap" style={{ fontSize: 12 }}>{ADJ_KIND_KO[a.kind] ?? a.kind}</td>
                          <td className="num nowrap" style={{ fontWeight: 700, color: a.amount >= 0 ? '#c2410c' : 'var(--leaf-700)' }}>
                            {a.amount >= 0 ? '+' : ''}{fmt.won(a.amount)}원
                          </td>
                          <td style={{ fontSize: 12 }}>{a.reason ?? '-'}</td>
                          <td className="nowrap cell-mono" style={{ fontSize: 11 }}>{a.created_by ?? '-'}</td>
                          <td className="nowrap cell-mono" style={{ fontSize: 11 }}>{a.created_at}</td>
                          <td className="nowrap" style={{ display: 'flex', gap: 4 }}>
                            <button type="button" onClick={() => toggleAdjAtt(a.id)}
                                    className="filter-ghost" style={{ padding: '2px 8px', fontSize: 11 }}
                                    title="증빙 첨부 목록">📎</button>
                            {a.kind !== 'device_fee' && (
                              <button type="button" onClick={() => reverseAdjustment(a.id)}
                                      className="filter-ghost" style={{ padding: '2px 8px', fontSize: 11, color: 'var(--warn)' }}
                                      title="반대 부호로 반전 등록">반전</button>
                            )}
                          </td>
                        </tr>
                        {attOpen && (
                          <tr>
                            <td colSpan={7} style={{ background: 'var(--surface-alt, #fafbfa)', padding: 8 }}>
                              {!atts ? (
                                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>불러오는 중…</div>
                              ) : atts.length === 0 ? (
                                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>첨부 없음</div>
                              ) : (
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                  {atts.map(att => (
                                    <div key={att.id} style={{ border: '1px solid var(--border, #e6ece9)', borderRadius: 6, padding: 6, background: '#fff', fontSize: 11, minWidth: 140 }}>
                                      <a href="#" onClick={async (e) => {
                                        e.preventDefault();
                                        const tok = localStorage.getItem('supportree_token');
                                        const url = `${(import.meta.env.VITE_API_BASE as string | undefined) ?? 'https://supportree-api.rhksdn4446.workers.dev'}/merchants/${encodeURIComponent(routeMid!)}/adjustments/${a.id}/attachments/${att.id}`;
                                        const r = await fetch(url, { headers: tok ? { Authorization: `Bearer ${tok}` } : undefined });
                                        if (!r.ok) { alert('열기 실패'); return; }
                                        const b = await r.blob();
                                        const u = URL.createObjectURL(b);
                                        window.open(u, '_blank');
                                        setTimeout(() => URL.revokeObjectURL(u), 60_000);
                                      }}>
                                        📎 {att.filename}
                                      </a>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--muted)', marginTop: 2 }}>
                                        <span>{(att.byte_size / 1024).toFixed(0)}KB</span>
                                        <button type="button" onClick={() => deleteAdjAtt(a.id, att.id)}
                                                className="filter-ghost" style={{ padding: '1px 6px', fontSize: 10, color: 'var(--warn)' }}>삭제</button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </fieldset>
      )}

      {isEdit && (
        <fieldset style={{ marginTop: 18 }}>
          <legend>단말기 통신비 (월 자동 반영)</legend>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10, lineHeight: 1.6 }}>
            익일 단말기를 사용하는 가맹점의 경우, 매월 1일 통신비(기본 11,000원)를 잔여액에 자동 반영할 수 있습니다.
            대상 등록/해제만 여기서 하고, 실제 월별 반영은 「단말기 통신비 자동 반영」 기능에서 한꺼번에 처리합니다.
            동일 월 중복 반영은 자동 차단되며 조정 이력에 기록됩니다.
          </div>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input type="checkbox" checked={hasDevice}
                   onChange={toggleHasDevice} disabled={hasDeviceSaving} />
            <b>단말기 사용 대상</b>
            {hasDevice && <span style={{ color: 'var(--leaf-700)', fontSize: 12 }}>· 매월 1일 통신비 자동 반영</span>}
          </label>

          <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border-soft, #eef2f0)' }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>단말기 반납 상태</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10, lineHeight: 1.6 }}>
              계약 종료 후 단말기 회수 여부를 관리합니다. 상태는 가맹점 관리 목록에서 컬럼·필터로 함께 확인 가능합니다.
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div>
                <label style={{ fontSize: 12 }}>반납 상태</label>
                <select value={deviceReturnStatus}
                        onChange={e => setDeviceReturnStatus(e.target.value as any)}
                        style={{ display: 'block', marginTop: 4, minWidth: 140 }}>
                  <option value="unreturned">미반납</option>
                  <option value="returned">반납완료</option>
                  <option value="not_applicable">해당없음</option>
                </select>
              </div>
              {deviceReturnStatus === 'returned' && (
                <div>
                  <label style={{ fontSize: 12 }}>반납일</label>
                  <input type="date" value={deviceReturnDate}
                         onChange={e => setDeviceReturnDate(e.target.value)}
                         style={{ display: 'block', marginTop: 4 }} />
                </div>
              )}
              <button type="button" className="btn" onClick={saveDeviceReturn} disabled={deviceReturnSaving}>
                {deviceReturnSaving ? '저장 중…' : '반납 상태 저장'}
              </button>
            </div>
          </div>
        </fieldset>
      )}

      {/* MID 변경/연결 모달 — 수정 화면에서 MID 변경 시 별도 확인 절차. */}
      <Modal
        open={midChangeOpen}
        title={`MID 변경/연결 — ${form.name}`}
        onCancel={() => !midChanging && setMidChangeOpen(false)}
        onOk={doMidChange}
        okText="변경"
        okType="danger"
        cancelText="취소"
        confirmLoading={midChanging}
        destroyOnHidden
        width={520}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13 }}>
          <div>
            현재 MID: <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{form.mid}</span>
          </div>
          <div>
            <div style={{ marginBottom: 4 }}>새 MID</div>
            <Input value={midChangeValue}
                   onChange={e => setMidChangeValue(e.target.value)}
                   placeholder="영문/숫자/._- 만 허용"
                   autoFocus
                   onPressEnter={doMidChange} />
          </div>
          <div style={{ padding: '8px 10px', background: '#fef3c7', color: '#854d0e', borderRadius: 6, fontSize: 12, lineHeight: 1.55 }}>
            MID는 <b>결제 원장·정산 스냅샷·이체 이력</b>의 연결 기준값입니다.
            변경 시 관련 <b>거래·정산·이체</b> 행의 mcht_id도 자동 갱신됩니다.
            새 MID가 이미 다른 가맹점으로 등록되어 있으면 <b>거부</b>됩니다 (MID 하나 = 가맹점 하나 절대규칙).
          </div>
        </div>
      </Modal>
    </div>
  );
}
